import { VisioFields } from '@agent/core/channel-digit/visio/models/visio-request-queries';
import { BasketVisioLabels, Csr } from '@agent/core/channel-digit/visio/models/visio.requests.interfaces';
import { AppointmentStatus } from '@commons/models/appointment';
import { copy } from 'angular';

import { ChannelType, Skill } from '@contact/upgrade';

import {
  ADAPTER_BASKET_REQUEST_IDS,
  ADAPTER_BASKET_REQUESTS,
  BasketCategoryModel,
  BasketChannelModel,
  BasketColumnModel,
  BasketLabelModel,
  BasketModel,
} from '../basket.model';
import { BasketCategoryType } from '../models/basket';

export enum AppointmentType {
  APPOINTMENT = 'APPOINTMENT',
  INVITE = 'INVITE',
}

export interface RequestModel extends BasketModel {
  readonly id: string;
  readonly idContact: string;
  readonly contactFirstName: string;
  readonly contactLastName: string;
  readonly phoneNumber: string;
  readonly startDate: Date;
  readonly endDate: Date;
  readonly status: AppointmentStatus;
  readonly csr: Csr;
  readonly participants: Csr[];
  readonly awaitingParticipants: Csr[];
  readonly refusingParticipants: Csr[];
  readonly skillList: any[];
  readonly comment: string;
  readonly targets: string[];
  readonly reason: string;
  readonly recycled: boolean;
  readonly type: AppointmentType;
  readonly flowUuid: string;
  readonly allTargets: string[];
  readonly isOperational: boolean;
  readonly campaignUuid: string;
}

export interface QueryFilterModel {
  // if filter is present
  readonly hasFilters: boolean;
  // contact filter
  readonly contact: string;
  // agent filter
  readonly agent: string;
  // participant filter
  readonly participant: string;
  // skill list filter
  readonly skills: Skill[];
  // appointment type
  readonly appointmentType: AppointmentType | undefined;
  // period filter begin time
  readonly beginTime: Date;
  // period filter end time
  readonly endTime: Date;
  // if period filter enabled
  readonly isPeriodFilterEnabled: boolean;
}

export interface VisioColumnModel extends BasketColumnModel {
  // colunm must be display label
  readonly showLabel: boolean;
}

export interface VisioCategoryModel extends BasketCategoryModel<VisioColumnModel> {}

export interface VisioLabelModel extends BasketLabelModel<RequestModel> {
  // filter label
  readonly labelName: string;
}

export interface VisioChannelModel extends BasketChannelModel<VisioLabelModel, VisioCategoryModel> {
  // filter query
  readonly queryFilters: QueryFilterModel;
}

/**
 * initial state for query filter
 */
export const INITIAL_STATE_QUERY_FILTER: QueryFilterModel = {
  hasFilters: false,
  agent: '',
  skills: [],
  participant: '',
  contact: '',
  appointmentType: undefined,
  beginTime: new Date(),
  endTime: new Date(),
  isPeriodFilterEnabled: false,
};

/**
 * initial state for personal category
 */
const INITIAL_STATE_CATEGORY_PERSONAL: VisioCategoryModel = {
  category: BasketCategoryType.PERSONAL,
  lastLabelSelected: BasketVisioLabels.ALL,
  sort: {
    defaultKey: VisioFields.START_DATE,
    key: VisioFields.START_DATE,
    order: 'asc',
  },
  columns: [
    {
      label: 'appointmentType',
      sortKey: VisioFields.TYPE,
      showLabel: true,
    },
    {
      label: 'date',
      sortKey: VisioFields.START_DATE,
      showLabel: true,
    },
    {
      label: 'contact',
      sortKey: VisioFields.CONTACT_FIRSTNAME,
      showLabel: true,
    },
    {
      label: 'appointmentStatus',
      sortKey: VisioFields.STATUS,
      showLabel: true,
    },
    {
      label: 'agent',
      sortKey: VisioFields.CSR_FIRSTNAME,
      showLabel: true,
    },
    {
      label: 'participant',
      sortKey: VisioFields.PARTICIPANT_FIRSTNAME,
      showLabel: true,
    },
    {
      label: 'skills',
      sortKey: VisioFields.SKILLS_LIST_NAME,
      showLabel: true,
    },
  ],
};

const INITIAL_STATE_CATEGORY_COMMON: VisioCategoryModel = {
  category: BasketCategoryType.COMMON,
  lastLabelSelected: BasketVisioLabels.ASSIGNABLE,
  sort: {
    defaultKey: VisioFields.START_DATE,
    key: VisioFields.START_DATE,
    order: 'asc',
  },
  columns: [
    {
      label: 'appointmentType',
      sortKey: VisioFields.TYPE,
      showLabel: true,
    },
    {
      label: 'date',
      sortKey: VisioFields.START_DATE,
      showLabel: true,
    },
    {
      label: 'contact',
      sortKey: VisioFields.CONTACT_FIRSTNAME,
      showLabel: true,
    },
    {
      label: 'appointmentStatus',
      sortKey: VisioFields.STATUS,
      showLabel: true,
    },
    {
      label: 'agent',
      sortKey: VisioFields.CSR_FIRSTNAME,
      showLabel: true,
    },
    {
      label: 'participant',
      sortKey: VisioFields.PARTICIPANT_FIRSTNAME,
      showLabel: true,
    },
    {
      label: 'skillList',
      sortKey: VisioFields.SKILLS_LIST_NAME,
      showLabel: true,
    },
  ],
};

/**
 * initial state for assignable label
 */
const INITIAL_STATE_LABEL_ALL: VisioLabelModel = {
  label: BasketVisioLabels.ALL,
  category: BasketCategoryType.PERSONAL,
  timestamp: 0,
  nbTotalRequests: 0,
  nbRequests: 0,
  requests: ADAPTER_BASKET_REQUESTS.getInitialState(),
  requestsInError: ADAPTER_BASKET_REQUEST_IDS.getInitialState(),
  requestsSelected: ADAPTER_BASKET_REQUEST_IDS.getInitialState(),
  currentPage: 1,
  order: 1,
  labelName: 'agent:contact.workspace.basket.visio.labels.all',
  showPagination: true,
};

/**
 * initial state for transfered label
 */
const INITIAL_STATE_LABEL_CONFIRMED: VisioLabelModel = {
  label: BasketVisioLabels.CONFIRMED,
  category: BasketCategoryType.PERSONAL,
  timestamp: 0,
  nbTotalRequests: 0,
  nbRequests: 0,
  requests: ADAPTER_BASKET_REQUESTS.getInitialState(),
  requestsInError: ADAPTER_BASKET_REQUEST_IDS.getInitialState(),
  requestsSelected: ADAPTER_BASKET_REQUEST_IDS.getInitialState(),
  currentPage: 1,
  order: 2,
  labelName: 'agent:contact.workspace.basket.visio.labels.confirmed',
  showPagination: true,
};

/**
 * initial state for transfered label
 */
const INITIAL_STATE_LABEL_REQUESTED: VisioLabelModel = {
  label: BasketVisioLabels.REQUESTED,
  category: BasketCategoryType.PERSONAL,
  timestamp: 0,
  nbTotalRequests: 0,
  nbRequests: 0,
  requests: ADAPTER_BASKET_REQUESTS.getInitialState(),
  requestsInError: ADAPTER_BASKET_REQUEST_IDS.getInitialState(),
  requestsSelected: ADAPTER_BASKET_REQUEST_IDS.getInitialState(),
  currentPage: 1,
  order: 3,
  labelName: 'agent:contact.workspace.basket.visio.labels.requested',
  showPagination: true,
};

/**
 * initial state for CANCELLED label
 */
const INITIAL_STATE_LABEL_CANCELLED: VisioLabelModel = {
  label: BasketVisioLabels.CANCELLED,
  category: BasketCategoryType.PERSONAL,
  timestamp: 0,
  nbTotalRequests: 0,
  nbRequests: 0,
  requests: ADAPTER_BASKET_REQUESTS.getInitialState(),
  requestsInError: ADAPTER_BASKET_REQUEST_IDS.getInitialState(),
  requestsSelected: ADAPTER_BASKET_REQUEST_IDS.getInitialState(),
  currentPage: 1,
  order: 4,
  labelName: 'agent:contact.workspace.basket.visio.labels.cancelled',
  showPagination: true,
};

const INITIAL_STATE_LABEL_REQUEST_CANCELLED: VisioLabelModel = {
  label: BasketVisioLabels.REQUEST_CANCELLED,
  category: BasketCategoryType.PERSONAL,
  timestamp: 0,
  nbTotalRequests: 0,
  nbRequests: 0,
  requests: ADAPTER_BASKET_REQUESTS.getInitialState(),
  requestsInError: ADAPTER_BASKET_REQUEST_IDS.getInitialState(),
  requestsSelected: ADAPTER_BASKET_REQUEST_IDS.getInitialState(),
  currentPage: 1,
  order: 5,
  labelName: 'agent:contact.workspace.basket.visio.labels.request_cancelled',
  showPagination: true,
};

const INITIAL_STATE_LABEL_ASSIGNABLE: VisioLabelModel = {
  label: BasketVisioLabels.ASSIGNABLE,
  category: BasketCategoryType.COMMON,
  timestamp: 0,
  nbTotalRequests: 0,
  nbRequests: 0,
  requests: ADAPTER_BASKET_REQUESTS.getInitialState(),
  requestsInError: ADAPTER_BASKET_REQUEST_IDS.getInitialState(),
  requestsSelected: ADAPTER_BASKET_REQUEST_IDS.getInitialState(),
  currentPage: 1,
  order: 4,
  labelName: 'agent:contact.workspace.basket.visio.labels.assignable',
  showPagination: true,
};

/**
 * initial object value when store initializing
 */
export const INITIAL_STATE: VisioChannelModel = {
  channel: ChannelType.VISIO,
  loading: false,
  pageSize: 10,
  categorySelected: BasketCategoryType.PERSONAL,
  categories: {
    ids: [BasketCategoryType.PERSONAL, BasketCategoryType.COMMON],
    entities: {
      [BasketCategoryType.PERSONAL]: INITIAL_STATE_CATEGORY_PERSONAL,
      [BasketCategoryType.COMMON]: INITIAL_STATE_CATEGORY_COMMON,
    },
  },
  queryFilters: copy(INITIAL_STATE_QUERY_FILTER),
  labelSelected: BasketVisioLabels.ALL,
  labels: {
    ids: [
      BasketVisioLabels.ALL,
      BasketVisioLabels.CONFIRMED,
      BasketVisioLabels.REQUESTED,
      BasketVisioLabels.CANCELLED,
      BasketVisioLabels.ASSIGNABLE,
      BasketVisioLabels.REQUEST_CANCELLED,
    ],
    entities: {
      [BasketVisioLabels.ALL]: INITIAL_STATE_LABEL_ALL,
      [BasketVisioLabels.CONFIRMED]: INITIAL_STATE_LABEL_CONFIRMED,
      [BasketVisioLabels.REQUESTED]: INITIAL_STATE_LABEL_REQUESTED,
      [BasketVisioLabels.CANCELLED]: INITIAL_STATE_LABEL_CANCELLED,
      [BasketVisioLabels.ASSIGNABLE]: INITIAL_STATE_LABEL_ASSIGNABLE,
      [BasketVisioLabels.REQUEST_CANCELLED]: INITIAL_STATE_LABEL_REQUEST_CANCELLED,
    },
  },
};
